import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/auth';
import { UserPlus, Loader2 } from 'lucide-react';

interface StaffMember {
  id:         string;
  email:      string;
  role:       string;
  status:     string;
  createdAt:  string;
  lastSignIn: string | null;
}

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  owner:  { bg: 'rgba(201,168,76,0.15)', color: 'var(--admin-accent)' },
  admin:  { bg: 'rgba(91,155,213,0.1)',  color: 'var(--admin-info)'   },
  editor: { bg: 'var(--admin-success-bg)', color: 'var(--admin-success)' },
  viewer: { bg: 'rgba(100,100,100,0.1)', color: 'var(--admin-text-muted)' },
};

export default function StaffPage() {
  const [staff,   setStaff]   = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ data: StaffMember[] }>('/staff')
      .then(r => setStaff(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const roleBadge = (role: string) => {
    const s = ROLE_COLORS[role] ?? ROLE_COLORS.viewer!;
    return <span style={{ ...styles.badge, background: s.bg, color: s.color }}>{role}</span>;
  };

  const statusBadge = (status: string) => {
    const active = status === 'active';
    return (
      <span style={{
        ...styles.badge,
        background: active ? 'var(--admin-success-bg)' : 'var(--admin-warning-bg)',
        color:      active ? 'var(--admin-success)'    : 'var(--admin-warning)',
      }}>
        {status}
      </span>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Staff</h1>
          <p style={styles.pageSubtitle}>{staff.length} members</p>
        </div>
        <button style={styles.addBtn}>
          <UserPlus size={16} /> Invite Staff
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Member</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Last sign in</th>
              <th style={styles.th}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={styles.loadingCell}>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--admin-text-muted)' }} />
              </td></tr>
            ) : staff.length === 0 ? (
              <tr><td colSpan={5} style={styles.emptyCell}>No staff members found.</td></tr>
            ) : staff.map(s => (
              <tr key={s.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={styles.memberCell}>
                    <div style={{ ...styles.avatar, background: avatarColor(s.email) }}>
                      {s.email[0]!.toUpperCase()}
                    </div>
                    <span style={styles.email}>{s.email}</span>
                  </div>
                </td>
                <td style={styles.td}>{roleBadge(s.role)}</td>
                <td style={styles.td}>{statusBadge(s.status)}</td>
                <td style={styles.td}>
                  <span style={styles.date}>
                    {s.lastSignIn ? new Date(s.lastSignIn).toLocaleDateString('en-IN') : 'Never'}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={styles.date}>{new Date(s.createdAt).toLocaleDateString('en-IN')}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function avatarColor(email: string) {
  const colors = ['#C9A84C', '#4AADAD', '#D4806A', '#5B9BD5', '#9B7ED4'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length]!;
}

const styles: Record<string, React.CSSProperties> = {
  page:        { maxWidth: 900, margin: '0 auto' },
  pageHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  pageTitle:   { fontSize: 22, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2 },
  pageSubtitle:{ fontSize: 12, color: 'var(--admin-text-muted)' },
  addBtn:      { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: 'var(--admin-accent)', color: '#1A1A1A', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tableWrap:   { background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', overflowX: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '11px 16px', textAlign: 'left' as const, fontSize: 11, fontWeight: 600, color: 'var(--admin-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' as const, borderBottom: '1px solid var(--admin-border)', background: 'var(--admin-surface-2)' },
  tr:          { borderBottom: '1px solid var(--admin-border-light)' },
  td:          { padding: '13px 16px', verticalAlign: 'middle' as const, fontSize: 13 },
  loadingCell: { padding: 40, textAlign: 'center' as const },
  emptyCell:   { padding: 40, textAlign: 'center' as const, color: 'var(--admin-text-muted)', fontSize: 13 },
  memberCell:  { display: 'flex', alignItems: 'center', gap: 10 },
  avatar:      { width: 32, height: 32, borderRadius: '50%', color: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  email:       { fontWeight: 500, color: 'var(--admin-text)' },
  badge:       { display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' as const },
  date:        { fontSize: 12, color: 'var(--admin-text-muted)' },
};
